import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Avatar,
} from '@mui/material';
import { useChatbot} from './hooks/useChatbot';
import { useChatSession } from './hooks/useChatSession';
import ChatInterface from './ChatInterface';
import { Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon, Refresh as RefreshIcon, Remove as MinimizeIcon } from '@mui/icons-material';
import ConfirmationDialog from '../common/ConfirmationDialog';
import DetailedSkillAnalysisModal from '../modals/DetailedSkillAnalysisModal';

import { useChatbot as useChatbotContext } from '../../contexts/ChatbotContext';
import { useTranslation } from 'react-i18next';
import miroAvatar from '../../assets/miro-avatar.png';

const ChatbotContainer = ({ isEnlarged, onToggleEnlarge }) => {

  const { t } = useTranslation();
  

  const { session, sessionLoading, createSession, error: sessionError } = useChatSession();
  const {
    messages,
    isLoading,
    availableActions,
    error,
    sendMessage,
    addMessage,
    resetChat
  } = useChatbot(session?.session_id);
  const { toggleChatbot } = useChatbotContext();

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState({ candidates: [], summary: '' });

  // Create session on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session && !sessionLoading) {
      createSession('general', {}).catch(err => {
        console.error('Failed to create session:', err);
        setSnackbar({
          open: true,
          message: t('chatbot.errors.generic', { error: error.message }),
          severity: 'error'
        });
      });
    }
  }, [session, sessionLoading, createSession,error?.message,t]);


  const handleFileUpload = async (file) => {
    try {
      // Add user message to UI immediately
      addMessage('user', t('chatbot.upload.userUploaded', { fileName: file.name }));

      // Use the existing chatbot API service
      const { chatbotApi } = await import('./services/chatbotApi');

      // Upload resume using the proper API client
      // const result = await chatbotApi.uploadResume(session?.session_id, file);
      await chatbotApi.uploadResume(session?.session_id, file);

      // Add bot response asking what to do with the resume
      addMessage('bot', t('chatbot.upload.botUploaded', { fileName: file.name }), {
        suggestions: [
            t('chatbot.upload.suggestions.analyzeSkills'),
            t('chatbot.upload.suggestions.findJobs'),
            t('chatbot.upload.suggestions.createProfile'),
            t('chatbot.upload.suggestions.extractInfo'),
            t('chatbot.upload.suggestions.generateReport')
        ]
      });
      
    } catch (error) {
      console.error('File upload error:', error);
      addMessage('bot', t('chatbot.upload.error', { error: error.message }));
    }
  };

  const handleActionClick = async (action) => {
    try {
      if (typeof action === 'object' && action.type === 'VIEW_CANDIDATES') {
        setAnalysisData({
          candidates: action.results,
          summary: action.summary
        });
        setIsAnalysisModalOpen(true);
      } else {
        await sendMessage(action);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (sessionLoading || !session) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {sessionLoading ?  t('chatbot.session.creating')
                             : t('chatbot.session.loading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || sessionError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || sessionError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
        borderRadius: 'inherit',
        overflow: 'hidden'
      }}
    >
      {/* Dashboard-Style Top Navbar with Aptiview Pink-Purple & Blue Gradient Border */}
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          color: '#262626',
          p: 1.8,
          px: 2.2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '2.5px',
            background: 'linear-gradient(90deg, #0ea5e9 0%, #d946ef 100%)',
          },
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Box
          sx={{
            p: '2px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(217, 70, 239, 0.25)'
          }}
        >
          <Avatar
            src={miroAvatar}
            sx={{
              width: 36,
              height: 36,
              border: '2px solid white'
            }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight="700" color="#1e293b" sx={{ lineHeight: 1.2 }}>
            {t('chatbot.heading')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.6, fontSize: '0.72rem', mt: 0.2 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#4ade80', display: 'inline-block', boxShadow: '0 0 8px #4ade80' }}></span>
            AI Recruiter Online
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={() => setIsResetConfirmOpen(true)} sx={{ color: '#525252', '&:hover': { color: '#0ea5e9', bgcolor: 'rgba(14, 165, 233, 0.1)' } }} title="Reset Chat">
            <RefreshIcon />
        </IconButton>
        <IconButton onClick={onToggleEnlarge} sx={{ color: '#525252', '&:hover': { color: '#0ea5e9', bgcolor: 'rgba(14, 165, 233, 0.1)' } }} title={isEnlarged ? "Restore" : "Maximize"}>
          {isEnlarged ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
        <IconButton onClick={toggleChatbot} sx={{ color: '#525252', '&:hover': { color: '#0ea5e9', bgcolor: 'rgba(14, 165, 233, 0.1)' } }} title="Minimize">
            <MinimizeIcon />
        </IconButton>
      </Box>

      <ConfirmationDialog
        open={isResetConfirmOpen}
        onClose={() => setIsResetConfirmOpen(false)}
        onConfirm={() => {
            resetChat();
            createSession('general', {});
            setIsResetConfirmOpen(false);
        }}
        title={t('chatbot.reset.title')}
        message={t('chatbot.reset.message')}
      />

      {/* Chat Interface */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <ChatInterface
          messages={messages}
          isLoading={isLoading}
          onActionClick={handleActionClick}
          availableActions={availableActions}
          onSendMessage={sendMessage}
          onFileSelect={handleFileUpload}
        />
      </Box>

      <DetailedSkillAnalysisModal 
        open={isAnalysisModalOpen}
        onClose={() => setIsAnalysisModalOpen(false)}
        candidates={analysisData.candidates}
        summary={analysisData.summary}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatbotContainer;